import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  ButtonStyles,
  Drawer,
  ButtonSize,
  useTranslation,
  Notification,
  Theme,
  useSession,
  ButtonWidth,
} from '@nextgen-web-framework/all';
import {
  ContactType,
  Contact,
  OwnerType,
  ContactResponse,
  ExtendedContactType,
  ValidationType,
} from '@commerce-profile-webcomponents/sdk';
import { Logger } from '@nextgen-web-framework/core';
import AddressInput from '../AddressInput/AddressInput';
import ButtonWithLoading from '../Common/ButtonWithLoading/ButtonWithLoading';
import { getShippingAddressListUtil } from '../../utils/contactUtil';
import { useMyAccountAddressConfig } from '../../utils/masterdata';
import { AssetPath } from '../../utils/Constants';
import { getAddressConfig, processGCOAddress } from '../../utils/myAccountAddressUtils';
import { MyAccountExtEnum } from '../../utils/types/enum';
import { getExt } from '../../utils/myAccountProfileUtils';
import CommonErrorScreen from '../Common/CommonErrorFullScreen/CommonErrorScreen';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { ifThenElse } from '../../utils/helper';
import { AddressInputHandlerType } from '../AddressInput/types/propTypes';
import AddressList from './AddressList/AddressList';
import type { AddressBookProps } from './types/propTypes';
import AddNewAddressDisplayText from './AddNewAddressDisplayText/AddNewAddressDisplayText';
import styles from './AddressBook.module.scss';

//AddressBook is being used inside Checkout & Subscription, it uses AddressList component displays the list of Address cards & downlineAddress and Addreslist uses AddressCard component for showing cards.
const AddressBook = ({
  addressSelected,
  onConfirmClick,
  onDrawerClose,
  downlineAddressList,
  // To make this compatible with existing usage, set this to use default values from session -->
  accountNumber,
  profileId,
  // <-- end of default from session
  contactType = ContactType.Shipping,
  useAddressLookup,
  disableAddNew = false,
  enableSaveAsDefault = false,
  isLoading = false,
  allowEdit = true,
  disableDeliveryInstructions = false,
  isProxyOrder,
}: AddressBookProps) => {
  const t = useTranslation();
  const logger = Logger.getInstance();
  const addrConfigFile = useMyAccountAddressConfig();
  const addrConfig = getAddressConfig(addrConfigFile, contactType);
  // Get address limit
  const addrLimit = (getExt(MyAccountExtEnum.ADDRESS_BOOK_LIMIT, addrConfig.ext) as number) ?? 0;
  const allowEditOnDefault =
    (getExt(MyAccountExtEnum.ADDRESS_BOOK_ALLOW_EDIT_ON_DEFAULT, addrConfig.ext) as boolean) ?? false;
  const defaultTagTheme = (getExt(MyAccountExtEnum.DEFAULT_TAG_THEME, addrConfig.ext) as string) ?? Theme.PURPLE;

  const [isDownlineAddressOpen, setIsDownlineAddressOpen] = useState<boolean>(false);
  const [addressList, setAddressList] = useState<Contact[]>();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [showSkeletonLoader, setShowSkeletonLoader] = useState<boolean>(true);
  const [isAddressFormOpen, setIsAddressFormOpen] = useState<boolean>(false);

  const [isNewAddressForm, setIsNewAddressForm] = useState<boolean>(false);
  const [addressForForm, setAddressForForm] = useState<Contact>();
  const [editAddressForForm, setEditAddressForForm] = useState<Contact>();
  const [selectedAddressId, setSelectedAddressId] = useState<Contact>();

  const [inputFormCanSubmit, setInputFormCanSubmit] = useState<boolean>(false);

  const [showLoadingError, setShowLoadingError] = useState<boolean>(false);
  const [allowSelectInvalidAddress, setAllowSelectInvalidAddress] = useState<boolean>(false);
  const [downlineAddressListForProxy, setDownlineAddressListForProxy] = useState<Contact[]>();

  const { session } = useSession();
  const { id: defaultProfileId, aboNum: defaultAccountNum = '' } = session;

  // If profileId & accountNumber is not provided from prop, then we use value from session
  const currentProfileId = ifThenElse(profileId, profileId, defaultProfileId);
  const currentAccountNum = accountNumber ?? defaultAccountNum;

  // OwnerType for shipping is Profile. Mailing & Billing is Account.
  const ownerType = contactType === ContactType.Shipping ? OwnerType.Profile : OwnerType.Account;
  const addressInputRef = useRef<AddressInputHandlerType>(null);

  useEffect(() => {
    setAllowSelectInvalidAddress(isDownlineAddressOpen);
  }, [isDownlineAddressOpen]);
  const apiErrorHandler = useGlobalErrorHandler();

  const keepSelectedAddressAtTop = (addresses: Contact[]) => {
    const indexOfSelectedAddress = addresses?.findIndex((address: Contact) => {
      if (addressSelected && addressSelected.ownerId === address.ownerId) {
        return address?.contactId === addressSelected?.contactId;
      } else {
        return address?.isDefault === true ? address?.contactId : '';
      }
    });
    indexOfSelectedAddress > 0 && addresses && addresses.unshift(addresses.splice(indexOfSelectedAddress, 1)[0]);
  };
  const setDownlineAddress = (downlineAddress: ExtendedContactType[], inDownLineView?: boolean) => {
    let downlineViewOpenFlag: boolean | undefined;
    if (!isProxyOrder) {
      !inDownLineView &&
        setIsDownlineAddressOpen((prev) => {
          downlineViewOpenFlag = !prev;
          return !prev;
        });
      setAddressList(downlineAddress);
    } else {
      setDownlineAddressListForProxy(downlineAddress);
    }
    downlineAddress.length > 0 && setSelectedAddress(downlineAddress, downlineViewOpenFlag);
  };
  const onConfirm = (value?: ExtendedContactType) => {
    // Adding profileName to payload for GCO if not found -->
    const addrValue = processGCOAddress(value, downlineAddressList);
    // <-- end of GCO profileName

    value && onConfirmClick && onConfirmClick(addrValue as ExtendedContactType);
  };
  const handleBackDownliner = () => {
    setIsDownlineAddressOpen((prev) => !prev);
    getUserList();
  };
  const onFormSubmit = (value?: ExtendedContactType) => {
    if (value?.contactId === selectedAddressId?.contactId || isNewAddressForm) {
      value && onConfirmClick && onConfirmClick(value);
    } else {
      setIsAddressFormOpen(false);
      setIsNewAddressForm(false);
      setShowNotification(true);
      getUserList();
    }
  };
  const onTextlinkClick = () => {
    setIsNewAddressForm(true);
    setIsAddressFormOpen(true);
    setAddressForForm({} as Contact);
  };

  const onAddressEdit = (value: Contact) => {
    setIsAddressFormOpen(true);
    setIsNewAddressForm(false);
    setAddressForForm(value);
    setEditAddressForForm(value);
  };

  const getUserList = async () => {
    try {
      const ownerId = ownerType === OwnerType.Profile ? currentProfileId : currentAccountNum;
      const res: ContactResponse = await getShippingAddressListUtil(
        contactType,
        ownerId as string,
        currentAccountNum,
        apiErrorHandler,
      );
      setAddressList(res.contacts);
      setShowSkeletonLoader(false);
      setSelectedAddress(res.contacts ?? []);
    } catch (_error) {
      // Show error screen
      setShowLoadingError(true);
      logger.error('Error in getting address list', _error);
    }
  };
  const setSelectedAddress = (addresses: Contact[], isDownlineOpen?: boolean) => {
    keepSelectedAddressAtTop(addresses);
    const validAddressSelected =
      addressSelected && addresses.find((address: Contact) => address?.contactId === addressSelected?.contactId);

    const defaultAddress = addresses && addresses.find((address: Contact) => address?.isDefault);
    const isDownliner = addressSelected && addresses[0].ownerId !== addressSelected.ownerId;
    if (isDownliner) {
      if (isDownlineOpen) {
        setSelectedAddressId(validAddressSelected || defaultAddress);
      } else {
        setSelectedAddressId(addressSelected);
      }
    } else {
      setSelectedAddressId(validAddressSelected || defaultAddress);
    }
  };
  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    setInputFormCanSubmit(!isAddressFormOpen);
  }, [isAddressFormOpen]);

  const handleFormCanSubmit = (canSubmit: boolean) => {
    if (canSubmit) {
      setAddressForForm(addressInputRef.current?.getAddress());
    }

    setInputFormCanSubmit(canSubmit);
  };
  const addressHeadingLabel = useMemo(() => {
    if (isAddressFormOpen && !isNewAddressForm) {
      return 'profile.addressBook.editAddressLabel';
    } else if (isNewAddressForm) {
      return 'profile.addNewAddress.heading';
    } else {
      return 'profile.addressBook.heading';
    }
  }, [isAddressFormOpen, isNewAddressForm]);

  const showBackButton = useMemo(
    () => isAddressFormOpen || isNewAddressForm || isDownlineAddressOpen,
    [isAddressFormOpen, isNewAddressForm, isDownlineAddressOpen],
  );

  const buttonFormId = useMemo(
    () => (isAddressFormOpen || isNewAddressForm ? 'shipping-address-form-id' : ''),
    [isAddressFormOpen, isNewAddressForm],
  );

  // To handle styles missing in drawer
  const buttonGroupStyle = useMemo(
    () => ({
      width: '-webkit-fill-available',
      display: 'grid',
      columnGap: '8px',
      gridTemplateColumns: showBackButton ? 'calc(50% - 4px) calc(50% - 4px)' : '',
      padding: '0px 16px',
    }),
    [showBackButton],
  );

  // To limit number of addresses
  const addrLength = addressList?.length ?? 0;
  const allowAddNewAddr = !disableAddNew && (addrLimit === 0 || addrLimit > addrLength);

  const isBadAddress = (address: Contact) => address?.validationStatus === ValidationType.NotValidated;
  // To consider addressForForm?.validationStatus === ValidationType.NotValidated ?

  const disableButton =
    (isAddressFormOpen && isBadAddress(addressForForm as Contact)) ||
    (!isAddressFormOpen && isBadAddress(selectedAddressId as Contact));

  return (
    <div className={styles['address-book-container']}>
      <Drawer
        header={<span>{t(addressHeadingLabel)}</span>}
        useReactPortal={true}
        portalContainerId={'react-portal-drawer'}
        closeIcon
        className={styles['address-book-drawer']}
        handleScroll={false}
        onClose={onDrawerClose}
        bottomGap={0}
        footer={
          <div className={styles['confirm-button-group']} data-testId="footer-button-id" style={buttonGroupStyle}>
            {showBackButton && (
              <div className={styles['back-button']}>
                <Button
                  data-testId="back-button-id"
                  title={t('profile.addressBook.backButton')}
                  buttonStyle={ButtonStyles.OUTLINE}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  size={ButtonSize.MEDIUM}
                  onClick={() => {
                    if (isDownlineAddressOpen) {
                      handleBackDownliner();
                    } else {
                      setIsAddressFormOpen(false);
                      setIsNewAddressForm(false);
                    }
                  }}
                />
              </div>
            )}
            <div className={styles['confirm-button']}>
              <ButtonWithLoading
                data-testId="confrim-button-id"
                form={buttonFormId}
                title={t('profile.addressBook.confirmButton')}
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.MEDIUM}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                disabled={
                  showSkeletonLoader ||
                  (isDownlineAddressOpen && addressList?.length === 0) ||
                  !inputFormCanSubmit ||
                  disableButton
                }
                onClick={() => {
                  if (!isAddressFormOpen) {
                    onConfirm(selectedAddressId ? selectedAddressId : addressSelected);
                  }
                }}
                isLoading={isLoading}
              />
            </div>
          </div>
        }
        closeOnBackdropClick
      >
        <div className={styles['address-form-drawer-body']}>
          {isAddressFormOpen ? (
            <AddressInput
              address={addressForForm}
              hideConfirmButton={true}
              isEdit={!isNewAddressForm}
              callback={onFormSubmit}
              accountNumber={currentAccountNum}
              ownerId={currentProfileId}
              contactType={contactType}
              ownerType={ownerType}
              showDefaultAddressCheckbox={enableSaveAsDefault}
              // Use mobile view in drawer, because it's showing in single column
              useMobileView={true}
              lookupEnabled={useAddressLookup}
              isLoading={isLoading}
              updateDisableButton={handleFormCanSubmit}
              disableDeliveryInstructions={disableDeliveryInstructions}
              ref={addressInputRef}
              {...(isBadAddress(editAddressForForm as Contact) &&
                defaultProfileId != editAddressForForm?.ownerId && {
                  header: (
                    <div className={styles['notification']}>
                      <Notification
                        hasNotificationIcon={true}
                        theme={Theme.WARNING}
                        message={t('profile.addressBook.noteForDownLineAddressUpdate')}
                      />
                    </div>
                  ),
                  enableErrorFieldsOnly: true,
                })}
            />
          ) : (
            <>
              {!isDownlineAddressOpen && allowAddNewAddr && (
                <AddNewAddressDisplayText onTextlinkClick={onTextlinkClick} isInsideDrawer={true} />
              )}
              <AddressList
                onAddressEdit={onAddressEdit}
                addressList={addressList as Contact[]}
                addressListOfProxy={downlineAddressListForProxy as Contact[]}
                onAddressSelect={setSelectedAddressId}
                isSelectedAddress={selectedAddressId}
                showSkeletonLoader={showSkeletonLoader}
                downlineAddressList={downlineAddressList}
                setDownlineAddress={setDownlineAddress}
                isDownlineAddressOpen={isDownlineAddressOpen}
                showHeading={true}
                allowEdit={allowEdit}
                allowSelectInvalidAddress={allowSelectInvalidAddress}
                isProxyOrder={isProxyOrder}
                allowEditOnDefault={allowEditOnDefault}
                defaultTagTheme={defaultTagTheme as Theme}
              />
              {showNotification && (
                <div className={styles['profile-notification-container']}>
                  <Notification
                    message={t('profile.addressBook.notification')}
                    theme={Theme.SUCCESS}
                    hasBoldText={true}
                    hasNotificationIcon={true}
                    onCloseClick={() => setShowNotification(false)}
                    data-testid="test-notification-id"
                  />
                </div>
              )}
            </>
          )}
          {showLoadingError && (
            <CommonErrorScreen
              imageSrc={AssetPath.ERROR_IMAGE}
              title={t('profile.common.somethingWentWrong')}
              description={t('profile.common.pleaseTryAgain')}
              buttonCTA={onDrawerClose}
              buttonText={t('profile.addressBook.backButton')}
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default AddressBook;
